/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { ToastContainer } from 'react-toastify'
import { fetchData, formatPriceWithCommas, RANDOM_COLORS, sanitizeNumericInput, toasterrormsg, toastsuccessmsg, useMenuIdPermissions } from '../../../reusable'
import { PageTitle } from '../../../_metronic/layout/core'
import { KTIcon, toAbsoluteUrl } from '../../../_metronic/helpers'
import { Tr } from '../../../_metronic/i18n/Tr'

import { SponserSetting, SponserTargetMaster } from '../types/adsSettings'
import { NoDataFound } from '../components/NoDataFound'

const buildInitialValues = (data: SponserSetting[]) => {
  const vals: Record<string, string> = {}
  data.forEach((item) => {
    vals[`amount_${item.sponsersettingId}`] = item.perDayPrice != null ? String(item.perDayPrice) : ''
    vals[`maxSponser_${item.sponsersettingId}`] = item.maxSponser != null ? String(item.maxSponser) : ''
  })
  return vals
}

const buildValidationSchema = (data: SponserSetting[]) => {
  const shape: Record<string, any> = {}
  data.forEach((item) => {
    shape[`amount_${item.sponsersettingId}`] = Yup.number()
      .typeError('Must Be A Number')
      .min(1, 'Must Be At Least 1')
      .max(99999999, 'Maximum 99,999,999 Allowed')
      .required('Amount Is Required')
    shape[`maxSponser_${item.sponsersettingId}`] = Yup.number()
      .typeError('Must Be A Whole Number')
      .integer('Must Be A Whole Number')
      .min(1, 'At Least 1 Required')
      .max(50, 'Max 50 Are Allowed')
      .required('Max Search Ads Is Required')
  })
  return Yup.object().shape(shape)
}

const ViewSearchAdSettings = () => {
  const t = Tr()
  const { menuIdEdit } = useMenuIdPermissions()

  const [sponserSettings, setSponserSettings] = useState<SponserSetting[]>([])
  const [imageBaseUrl, setImageBaseUrl] = useState('')
  const [dataLoading, setDataLoading] = useState(true)
  const [saveLoading, setSaveLoading] = useState(false)

  const [targetingOptions, setTargetingOptions] = useState<SponserTargetMaster[]>([])
  const [targetSaveLoading, setTargetSaveLoading] = useState(false)
  const tmp = sessionStorage.getItem('admin')
  const adminId = tmp ? JSON.parse(tmp).adminId : ''

  const sponserSettingListApiCall = async () => {
    try {
      const param = {
        sort: "desc",
        sortBy: "sponsersettingId"
      }
      const res = await fetchData('admin/sponser/setting/list', param)
      if (res.status == '200') {
        const data = res?.data?.list ?? res?.data ?? []
        setSponserSettings(Array.isArray(data) ? data : [])
        if (res?.url) setImageBaseUrl(res.url)
      } else {
        toasterrormsg(res?.message || 'An error occurred')
      }
    } catch (error) {
      console.error('Error fetching sponser settings:', error)
    } finally {
      setDataLoading(false)
    }
  }

  const targetMasterListApiCall = async () => {
    try {
      const res = await fetchData('admin/sponser/targetmaster/list', {})
      if (res.status == '200') {
        setTargetingOptions(res?.data?.list ?? [])
      } else {
        toasterrormsg(res?.message || 'An error occurred')
      }
    } catch (error) {
      console.error('Error fetching target master list:', error)
    }
  }

  const saveTargetingSettings = async () => {
    setTargetSaveLoading(true)
    const param = {
      adminId,
      data: targetingOptions.map((opt) => ({
        sponsertargetmasterId: opt.sponsertargetmasterId,
        label: opt.label,
        key: opt.key,
        inputType: opt.inputType,
        description: opt.description,
        isActive: opt.isActive,
      })),
    }
    try {
      const res = await fetchData('admin/sponser/targetmaster/update', param)
      if (res.status == '200') {
        toastsuccessmsg(res.message)
        targetMasterListApiCall()
      } else {
        toasterrormsg(res?.message || 'An error occurred')
      }
    } catch (error) {
      console.error('Error saving targeting settings:', error)
      toasterrormsg('Failed To Save Targeting Settings')
    } finally {
      setTargetSaveLoading(false)
    }
  }

  useEffect(() => {
    document.title = 'Insayr | Search Ad Settings'
    sponserSettingListApiCall()
    targetMasterListApiCall()
  }, [])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: buildInitialValues(sponserSettings),
    validationSchema: buildValidationSchema(sponserSettings),
    onSubmit: async (values) => {
      setSaveLoading(true)
      const param = {
        adminId,
        data: sponserSettings.map((item) => ({
          sponsersettingId: item.sponsersettingId,
          perDayPrice: values[`amount_${item.sponsersettingId}`],
          maxSponser: values[`maxSponser_${item.sponsersettingId}`],
          isActive: item.isActive,
        })),
      }

      try {
        const res = await fetchData('admin/sponser/setting/update', param)
        if (res.status == '200') {
          toastsuccessmsg(res.message)
          sponserSettingListApiCall()
        } else {
          toasterrormsg(res?.message || 'An error occurred')
        }
      } catch (error) {
        console.error('Error updating sponser settings:', error)
        toasterrormsg('Failed To Update Settings')
      } finally {
        setSaveLoading(false)
      }
    },
  })

  const getStableColor = (id: number) => {
    return RANDOM_COLORS[id % RANDOM_COLORS.length]
  }

  if (dataLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 300 }}>
        <span className='spinner-border text-primary' style={{ width: 40, height: 40 }} />
      </div>
    )
  }


  return (
    <>
      <ToastContainer />
      <PageTitle breadcrumbs={[]}>{t('Search Ad Settings')}</PageTitle>

      <div className='row justify-content-center'>
        <div className='col-12 mb-4'>
          <div className='card p-5'>
            <div className='card-header'>
              <div className='card-title m-0'>
                <h3 className='fw-bolder m-0'>{t('Search Ad Placement Settings')}</h3>
              </div>
            </div>

            <div className='card-body p-9'>
              {/* ── Info Tips ── */}
              <div className='sponser-settings-info-tips'>
                <div className='sponser-settings-info-tip sponser-settings-info-tip--warning'>
                  <div className='sponser-settings-info-tip__icon'>
                    <KTIcon iconName='information-5' className='fs-3 text-warning' />
                  </div>
                  <p className='sponser-settings-info-tip__text'>
                    <strong>{t('"Max Search Ads At A Time"')}</strong>{' '}
                    {t('refers to how many unique search ads (from different vendors) can run simultaneously in a placement slot.')}
                  </p>
                </div>
              </div>

              {/* ── Table ── */}
              <form onSubmit={formik.handleSubmit} noValidate>
                <div className='sponser-settings-table-wrapper'>
                  <table className='sponser-settings-table'>
                    <thead>
                      <tr>
                        <th className='sponser-settings-col-placement'>{t('Placement')}</th>
                        <th className='sponser-settings-col-amount'>{t('Amount Per Day (UZS)')}</th>
                        <th className='sponser-settings-col-maxads'>{t('Max Search Ads At A Time')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sponserSettings && sponserSettings.map((item) => {
                        const amtKey = `amount_${item.sponsersettingId}`
                        const maxKey = `maxSponser_${item.sponsersettingId}`
                        const color = getStableColor(item?.sponsersettingId ?? 0)
                        return (
                          <tr key={item.sponsersettingId}>
                            {/* Placement info */}
                            <td>
                              <div className='sponser-settings-row-placement'>
                                {item?.image ? (
                                  // <img
                                  //   src={imageBaseUrl + item.image}
                                  //   alt={item?.name || ''}
                                  //   style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
                                  //   onError={(e) => {
                                  //     e.currentTarget.onerror = null
                                  //     e.currentTarget.src = toAbsoluteUrl('/media/logos/mainLogo.png')
                                  //   }}
                                  // />
                                  <div className='my-3'>
                                    <div className='fw-bold fs-5 d-flex align-items-center justify-content-center'>
                                      <img className='w-50px h-50px' src={imageBaseUrl + item.image}
                                        alt={item?.name || ''}
                                        onError={(e) => {
                                          e.currentTarget.onerror = null
                                          e.currentTarget.src = toAbsoluteUrl('/media/logos/mainLogo.png')
                                        }}
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <div className={`sponser-settings-row-icon ${color?.bg ?? ''}`}>
                                    <KTIcon iconName='abstract-26' className={`fs-3 ${color?.text ?? ''}`} />
                                  </div>
                                )}
                                <div>
                                  <div className='sponser-settings-row-title'>{item?.name || '-'}</div>
                                </div>
                              </div>
                            </td>

                            {/* Amount Per Day */}
                            <td>
                              <div className='sponser-settings-input-group'>
                                <input
                                  type='text'
                                  name={amtKey}
                                  value={formatPriceWithCommas(formik.values[amtKey])}
                                  onChange={(e) => {
                                    const raw = sanitizeNumericInput(e.target.value)
                                    formik.setFieldValue(amtKey, raw)
                                  }}
                                  onBlur={formik.handleBlur}
                                  placeholder='e.g. 142,857'
                                  className={clsx('form-control', {
                                    'is-invalid':
                                      formik.touched[amtKey] &&
                                      formik.errors[amtKey],
                                  })}
                                  disabled={!menuIdEdit}
                                />
                                <span className='sponser-settings-input-suffix'>UZS</span>
                              </div>
                              {formik.touched[amtKey] &&
                                formik.errors[amtKey] && (
                                  <div className='fv-plugins-message-container'>
                                    <span role='alert' className='text-danger'>
                                      {formik.errors[amtKey] as string}
                                    </span>
                                  </div>
                                )}
                            </td>

                            {/* Max Sponsers At A Time */}
                            <td>
                              <input
                                type='text'
                                name={maxKey}
                                value={formik.values[maxKey] ?? ''}
                                onChange={(e) => {
                                  const raw = sanitizeNumericInput(e.target.value)
                                  formik.setFieldValue(maxKey, raw)
                                }}
                                onBlur={formik.handleBlur}
                                placeholder='e.g. 2'
                                className={clsx('form-control', {
                                  'is-invalid':
                                    formik.touched[maxKey] &&
                                    formik.errors[maxKey],
                                })}
                                disabled={!menuIdEdit}
                              />
                              {formik.touched[maxKey] &&
                                formik.errors[maxKey] && (
                                  <div className='fv-plugins-message-container'>
                                    <span role='alert' className='text-danger'>
                                      {formik.errors[maxKey] as string}
                                    </span>
                                  </div>
                                )}
                            </td>
                          </tr>
                        )
                      })}
                      {(!sponserSettings || sponserSettings.length === 0) && (
                        <NoDataFound message='No placement settings found' icon='setting-2' colSpan={3} variant='row' />
                      )}
                    </tbody>
                  </table>
                </div>

                {/* ── Save ── */}
                {menuIdEdit ? (
                  <div className='sponser-settings-save-bar'>
                    <button type='submit' disabled={saveLoading} className='btn btn-success'>
                      {!saveLoading && (
                        <span className='d-flex align-items-center gap-2'>
                          <KTIcon iconName='save-2' className='fs-3' />
                          {t('Save Settings')}
                        </span>
                      )}
                      {saveLoading && (
                        <span className='indicator-progress d-flex align-items-center'>
                          {t('Please wait...')}
                          <span className='spinner-border spinner-border-sm align-middle ms-2' />
                        </span>
                      )}
                    </button>
                  </div>
                ) : (
                  ''
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* ── Targeting Settings Card ── */}
      <div className='row justify-content-center'>
        <div className='col-12 mb-4'>
          <div className='card p-5'>
            <div className='card-header'>
              <div className='card-title m-0'>
                <h3 className='fw-bolder m-0'>{t('Audience Targeting Options')}</h3>
              </div>
            </div>

            <div className='card-body p-9'>
              {/* ── Note ── */}
              <div className='sponser-settings-note'>
                <KTIcon iconName='information-5' className='fs-3 text-warning mt-1' />
                <p>
                  {t(
                    'These are the master targeting options available across all search ads. When enabled, vendors will be able to select these targeting criteria while creating their search ads. Disabling an option will hide it from the search ad creation form for all vendors.'
                  )}
                </p>
              </div>

              {/* ── Table ── */}
              <div className='sponser-settings-table-wrapper'>
                <table className='sponser-settings-table'>
                  <thead>
                    <tr>
                      <th className='sponser-settings-col-placement'>{t('Targeting Criteria')}</th>
                      <th className='sponser-settings-col-categories'>{t('Status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {targetingOptions.map((opt) => {
                      const color = getStableColor(opt?.sponsertargetmasterId ?? 0)

                      return (
                        <tr key={opt.key}>
                          <td>
                            <div className='sponser-settings-row-placement'>
                              <div className={`sponser-settings-row-icon ${color?.bg ?? ''}`}>
                                <KTIcon
                                  iconName={opt?.iconName || 'abstract-26'}
                                  className={`fs-3 ${color?.text ?? ''}`}
                                />
                              </div>
                              <div>
                                <div className='sponser-settings-row-title'>{t(opt.label)}</div>
                                <div className='sponser-settings-row-desc'>{t(opt.description)}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className='form-check form-switch form-check-custom form-check-solid'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={opt.isActive}
                                onChange={() => {
                                  setTargetingOptions((prev) =>
                                    prev.map((item) =>
                                      item.sponsertargetmasterId === opt.sponsertargetmasterId
                                        ? { ...item, isActive: !item.isActive }
                                        : item
                                    )
                                  )
                                }}
                                disabled={!menuIdEdit}
                              />
                              <label className='form-check-label fw-semibold text-muted ms-3'>
                                {opt.isActive ? t('Enabled') : t('Disabled')}
                              </label>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                    {(!targetingOptions || targetingOptions.length === 0) && (
                      <NoDataFound message='No targeting options found' icon='focus' colSpan={2} variant='row' />
                    )}
                  </tbody>
                </table>
              </div>

              {/* ── Save ── */}
              {menuIdEdit ? (
                <div className='sponser-settings-save-bar'>
                  <button
                    type='button'
                    disabled={targetSaveLoading}
                    className='btn btn-success'
                    onClick={() => saveTargetingSettings()}
                  >
                    {!targetSaveLoading && (
                      <span className='d-flex align-items-center gap-2'>
                        <KTIcon iconName='save-2' className='fs-3' />
                        {t('Save Targeting Settings')}
                      </span>
                    )}
                    {targetSaveLoading && (
                      <span className='indicator-progress d-flex align-items-center'>
                        {t('Please wait...')}
                        <span className='spinner-border spinner-border-sm align-middle ms-2' />
                      </span>
                    )}
                  </button>
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export { ViewSearchAdSettings }
